import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listTranscriptTypes, type TranscriptType } from "@/features/reference/api";
import { ReferencePanel } from "./reference-panel";

/**
 * Documented stub (§3 seam → Tier 2.3). The transcript-type dictionary is read
 * here but *populated/parsed* by the Transcripts module; this surface exists so
 * the seam is visible, not silently dropped.
 */
export function TranscriptTypesPage() {
  const [items, setItems] = useState<TranscriptType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    listTranscriptTypes()
      .then(setItems)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <ReferencePanel
      title="Transcript Type Table"
      description="Account-transcript row-type dictionary."
      loading={loading}
      error={error}
    >
      <div className="border-primary/30 bg-primary/5 text-muted-foreground mb-4 rounded-md border border-dashed p-3 text-sm">
        Read-only seed. Full population and parsing arrive with{" "}
        <span className="font-medium">Tier 2.3 · Transcripts &amp; Tax Records</span>.
      </div>
      {items.length === 0 ? (
        <p className="text-muted-foreground text-sm">No transcript types seeded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead>Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-mono text-xs font-medium">{t.transType}</TableCell>
                  <TableCell>{t.shortDesc}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {[t.appliesToIndividuals && "Individuals", t.appliesToBusinesses && "Businesses"]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </TableCell>
                  <TableCell className="flex gap-1">
                    {t.monitor && <Badge variant="outline">Monitor</Badge>}
                    {t.download && <Badge variant="secondary">Download</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </ReferencePanel>
  );
}
